import type { ClassUpdateDetailDTO } from "@/orval/model";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { numericIdParamSchema, organizationIdParamsSchema } from "@/lib/route-params";
import { useParentClassUpdatesGetClass, useParentClassUpdatesGetUpdate } from "@/orval/parent-class-updates";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { DownloadIcon, PaperclipIcon } from "lucide-react";
import { MarkdownContent } from "@/components/markdown/markdown-content";

const paramsSchema = organizationIdParamsSchema.extend({
  classId: numericIdParamSchema,
  updateId: numericIdParamSchema,
});

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/classes_/$classId/updates/$updateId")({
  params: {
    parse: (params) => paramsSchema.parse(params),
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId, updateId } = Route.useParams();
  const { data: classDetail } = useParentClassUpdatesGetClass(organizationId, classId);
  const { data: update, isLoading } = useParentClassUpdatesGetUpdate(organizationId, classId, updateId);

  return (
    <div className="mx-auto w-full max-w-3xl">
      <PortalPageHeader
        className="mb-6"
        breadcrumbs={[
          { label: "Classes", to: "/organizations/$organizationId/parent/classes", params: { organizationId } },
          {
            label: classDetail?.name ?? update?.className ?? "Class",
            to: "/organizations/$organizationId/parent/classes/$classId",
            params: { organizationId, classId },
          },
          { label: update?.title ?? "Update" },
        ]}
        title={update?.title ?? "Update"}
      />

      <ClassUpdateArticle
        organizationId={organizationId}
        classId={classId}
        updateId={updateId}
        update={update}
        isLoading={isLoading}
      />
    </div>
  );
}

function ClassUpdateArticle({
  organizationId,
  classId,
  updateId,
  update,
  isLoading,
}: {
  organizationId: number;
  classId: number;
  updateId: number;
  update?: ClassUpdateDetailDTO;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (!update) {
    return null;
  }
  return (
    <article className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">{update.className}</Badge>
        {update.isEdited ? <Badge variant="secondary">Edited</Badge> : null}
        {update.attachmentCount > 0 ? (
          <Badge variant="outline">
            <PaperclipIcon className="mr-1 size-3" />
            {update.attachmentCount}
          </Badge>
        ) : null}
      </div>
      {update.studentNames.length ? (
        <div className="text-sm text-muted-foreground">For: {update.studentNames.join(", ")}</div>
      ) : null}
      {update.publishedAt ? (
        <div className="text-sm text-muted-foreground">
          Published {format(parseISO(update.publishedAt), "MMMM d, yyyy h:mm a")}
        </div>
      ) : null}
      {update.isEdited ? (
        <div className="text-sm text-muted-foreground">
          Edited {format(parseISO(update.updatedAt), "MMMM d, yyyy h:mm a")}
        </div>
      ) : null}
      {update.bodyMarkdown ? (
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <MarkdownContent>{update.bodyMarkdown}</MarkdownContent>
        </div>
      ) : null}
      {update.attachments.length ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">Attachments</h2>
          <div className="space-y-2">
            {update.attachments.map((attachment) => (
              <Button key={attachment.id} asChild variant="outline" size="sm">
                <Link
                  to="/organizations/$organizationId/parent/attachment-download/$classId/$updateId/$attachmentId"
                  params={{ organizationId, classId, updateId, attachmentId: attachment.id }}
                >
                  <DownloadIcon className="mr-2 size-4" />
                  {attachment.fileName}
                </Link>
              </Button>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
